import { OutlinedInput, OutlinedInputProps } from '@material-ui/core';
import { useMemo } from 'react';
import { useController } from 'react-hook-form';
import { useFieldControllerContext } from '../UI/Form/FieldControllerContext';
import FieldOutlinedInput from '../UI/Form/FieldOutlinedInput';
import { fieldHasError } from '../UI/Form/formUtils';

type FieldNumberInputProps = OutlinedInputProps & {
  onNumberChange?: (value: number) => void;
  min?: number;
  max?: number;
};

export const FieldNumberInput: React.FC<FieldNumberInputProps> = ({
  onChange,
  onBlur,
  onInput,
  onNumberChange,
  min,
  max,
  inputProps,
  ...props
}) => {
  const { name, control, inputLabel } = useFieldControllerContext();

  const {
    fieldState,
    field: { ref, value, onBlur: onFieldBlur, onChange: onFieldChange },
  } = useController({ name, control });

  const { eventHandlers } = useInputNumberFormatter({
    min,
    max,
    setValue: nextValue => {
      onFieldChange(nextValue);
      onNumberChange?.(toNumber(nextValue));
    },
  });

  return (
    <OutlinedInput
      id={name}
      name={name}
      inputRef={ref}
      label={inputLabel}
      onFocusCapture={eventHandlers.onFocusCapture}
      onBlurCapture={eventHandlers.onBlurCapture}
      onBlur={ev => {
        onBlur?.(ev);
        onFieldBlur();
      }}
      onInput={ev => {
        eventHandlers.onInput(ev);
        onInput?.(ev);
      }}
      onChange={ev => {
        eventHandlers.onChange(ev);
        onChange?.(ev);
      }}
      value={value || ''}
      error={fieldHasError(fieldState)}
      aria-describedby={`${name}-helper-text`}
      inputProps={{ ...inputProps }}
      autoComplete="off"
      {...props}
    />
  );
};

export default FieldOutlinedInput;

type UseInputNumberFormatterProps = {
  setValue?: (value: string) => void;
  maxFractionDigits?: number;
  min?: number;
  max?: number;
};

export const useInputNumberFormatter = ({
  setValue,
  maxFractionDigits = 8,
  min,
  max,
}: UseInputNumberFormatterProps) => {
  const eventHandlers = useMemo(
    () => ({
      onFocusCapture(event: React.FocusEvent<HTMLElement>) {
        const el = event.target as HTMLInputElement;
        el.value = formatter(el, { min, max }).value;
        setValue?.(el.value);
      },
      onBlurCapture(event: React.FocusEvent<HTMLElement>) {
        const el = event.target as HTMLInputElement;
        el.value = prettyFormatter(el.value, { maxFractionDigits });
        setValue?.(el.value);
      },
      onInput(event: React.FormEvent<HTMLElement>) {
        const el = event.target as HTMLInputElement;
        const { value, selectionStart, selectionEnd } = formatter(el, {
          min,
          max,
        });
        el.value = value;
        el.selectionStart = selectionStart;
        el.selectionEnd = selectionEnd;
      },
      onChange(event: React.ChangeEvent<HTMLElement>) {
        const el = event.target as HTMLInputElement;
        setValue?.(el.value);
      },
    }),
    [setValue, maxFractionDigits, min, max]
  );

  return { eventHandlers, formatter, prettyFormatter };
};

function prettyFormatter(value: string, { maxFractionDigits = 0 }) {
  return (
    Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFractionDigits,
    })
      // this method accepts a string but it is badly typed
      .formatToParts(value as unknown as number)
      .reduce((text, part) => {
        switch (part.type) {
          case 'group':
            return `${text} `;
          case 'decimal':
            return `${text}.`;
          default:
            return `${text}${part.value}`;
        }
      }, '')
  );
}

function formatter(
  state: { value: unknown; selectionStart?: number | null },
  options: { max?: number; min?: number }
) {
  const value = `${state.value}`.split('');
  const selectionStart = state.selectionStart || 0;
  let nextSelectionStart = selectionStart || 0;
  const insertedIndex = selectionStart - 1;
  const insertedChar = value[insertedIndex];

  if (insertedChar === ' ') nextSelectionStart += 1;

  if ('.,'.includes(insertedChar)) {
    value.slice(0, selectionStart - 1).forEach((c, i) => {
      if (/[^\d]/g.test(c)) {
        value[i] = '';
        nextSelectionStart -= 1;
      }
    });
    value[insertedIndex] = '.';
    value.slice(selectionStart).forEach((c, i) => {
      if (/[^\d]/g.test(c)) value[selectionStart + i] = '';
    });
  } else {
    let decimalSepFound = false;
    value.forEach((c, i) => {
      if (!decimalSepFound && '.,'.includes(c)) {
        value[i] = '.';
        decimalSepFound = true;
      } else if (/[^\d]/g.test(c)) {
        value[i] = '';
        nextSelectionStart -= 1;
      }
    });
  }
  let nextValue = value.join('');
  if (nextValue === '') {
    nextValue = '0';
    nextSelectionStart = 1;
  } else if (nextValue === '0') {
    nextSelectionStart = 1;
  } else if (nextValue === '.') {
    nextValue = '0.';
    nextSelectionStart += 1;
  } else if (nextValue.startsWith('0')) {
    nextValue = nextValue.slice(1);
    nextSelectionStart -= 1;
  }

  let nextSelectionEnd = nextSelectionStart;

  if (options.max !== undefined && toNumber(nextValue) > options.max) {
    nextValue = `${options.max}`;
    nextSelectionStart = 0;
    nextSelectionEnd = nextValue.length;
  }

  if (options.min !== undefined && toNumber(nextValue) < options.min) {
    nextValue = `${options.min}`;
    nextSelectionStart = 0;
    nextSelectionEnd = nextValue.length;
  }

  return {
    value: nextValue,
    selectionStart: nextSelectionStart,
    selectionEnd: nextSelectionEnd,
  };
}

function toNumber(value: string) {
  return Number(value.replace(/[^0-9.]/g, ''));
}
